import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { SplitColumns } from "@freightsimple/ui-core/ComponentLayout/SplitColumns";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { useState } from "react";
import { CreditCardsAddDialog } from "@/Features/CreditCards/Exports/Dialogs/CreditCardsAddDialog";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { PaymentsMethodWithIcon } from "../../CustomComponents/PaymentsMethodWithIcon";
import { PaymentsConfirmMakePaymentsDialog } from "../../Dialogs/PaymentsConfirmMakePaymentsDialog";
import { PaymentsMakePaymentsFailedDialog } from "../../Dialogs/PaymentsMakePaymentsFailedDialog";
import { PaymentsMakePaymentsReceiptDialog } from "../../Dialogs/PaymentsMakePaymentsReceiptDialog";
import { filterMakePaymentsInvoices } from "../../Helpers/paymentsMakePaymentsListHelpers";
import { paymentsMakePaymentsConfirmDisabledMessage } from "../../Helpers/paymentsMakePaymentsConfirmDisabledMessage";
import {
  paymentsChargeTimingText,
  paymentsConfirmButtonText,
} from "../../Helpers/paymentsMakePaymentsMethodText";
import { usePaymentsMakePaymentsFlow } from "../../Hooks/usePaymentsMakePaymentsFlow";
import { PaymentsMakePaymentsReviewPanel } from "../../Panels/PaymentsMakePaymentsReviewPanel";
import { PaymentsMakePaymentsInvoicesTable } from "../../Tables/PaymentsMakePaymentsInvoicesTable";
import { PaymentsStatusType } from "../../Types/PaymentsStatusType";

interface MakePaymentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipments: Shipment[];
  invoices: Invoice[];
  currency: string;
  refresh: () => Promise<void>;
}

/**
 * The whole Make Payments flow, owning its dialogs: the main dialog picks
 * invoices and a method; confirming swaps its body to a spinner while the
 * payment processes; the outcome then opens a second dialog on top — the
 * receipt or the failure notice — and closing that one closes both.
 *
 * Remount it per open (a `key` counter at the call site) so each run starts
 * fresh.
 */
export const PaymentsMakePaymentsDialog = ({
  open,
  onOpenChange,
  shipments,
  invoices,
  currency,
  refresh,
}: MakePaymentsDialogProps) => {
  const flow = usePaymentsMakePaymentsFlow({
    open,
    invoices,
    shipments,
    refresh,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  const { user } = useAuthentication();
  const companyName = user?.companyName ?? "";

  // Selecting invoices only makes sense before a payment is in flight — once
  // one is, the body is a spinner and the actions leave the footer. The
  // methods fetch does NOT gate this: invoices and shipments arrive via
  // props, so the table renders instantly and only the review panel's method
  // picker waits.
  const choosing = flow.status === PaymentsStatusType.Idle;

  const rows = filterMakePaymentsInvoices(
    flow.invoicesToPay,
    flow.shipmentMap,
    filterQuery,
  ).map((invoice) => ({
    invoice,
    // shipmentMap is guaranteed to hold it — invoicesToPay filters on that.
    shipment: flow.shipmentMap.get(invoice.shipmentId)!,
  }));

  const handlePaymentMethodSelected = (paymentMethodId: string) => {
    if (paymentMethodId === "add-credit-card") {
      setAddCardOpen(true);
      // Don't set it as selected payment method
      return;
    }
    flow.setSelectedPaymentMethod(paymentMethodId);
  };

  // Closing the receipt or the failure notice ends the flow: the main dialog
  // goes with it.
  const closeFlow = () => onOpenChange(false);

  const selectedPaymentMethodWithIcon = (
    <PaymentsMethodWithIcon
      selectedPaymentMethod={flow.selectedPaymentMethod}
      paymentMethodsState={flow.paymentMethodsState}
    />
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        width="full"
        fullHeight
        title="Make Payments"
        description={
          <>
            Select the invoices you want to pay.{" "}
            <Button
              size="link"
              inheritFont
              href="https://help.freightsimple.com/using-freightsimple/payments-and-invoices/make-payments"
            >
              Learn more
            </Button>
          </>
        }
        headerFilters={
          choosing ? (
            <SearchInput
              variant="dialog"
              value={filterQuery}
              onChange={setFilterQuery}
              placeholder="Filter invoices"
              data-testid="make-payments-filter"
            />
          ) : undefined
        }
        footer={
          choosing ? (
            <>
              <Button onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                primary
                disabled={
                  flow.includedInPayment.length <= 0 ||
                  flow.selectedPaymentMethod === undefined
                }
                disabledMessage={paymentsMakePaymentsConfirmDisabledMessage({
                  invoicesSelected: flow.includedInPayment.length,
                  loading: flow.loading,
                  paymentMethodsLoadFailed: flow.paymentMethodsLoadFailed,
                })}
                onClick={() => setConfirmOpen(true)}
                data-testid="confirm-payment"
              >
                Confirm Payment
              </Button>
            </>
          ) : undefined
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {choosing ? (
            <SplitColumns
              split="60/40"
              left={
                <PaymentsMakePaymentsInvoicesTable
                  rows={rows}
                  initialSelectedInvoiceIds={flow.initialSelectedInvoiceIds}
                  onSelectionChange={flow.setSelectedInvoiceIds}
                  filterQuery={filterQuery}
                  emptyMessage={
                    filterQuery.trim() !== ""
                      ? "No invoices match your filter"
                      : "You're all caught up! No invoices to pay at the moment."
                  }
                />
              }
              right={
                <PaymentsMakePaymentsReviewPanel
                  loading={flow.loading}
                  onRetryFailedLoad={
                    flow.paymentMethodsLoadFailed
                      ? flow.reloadPaymentMethods
                      : undefined
                  }
                  paymentMethodsState={flow.paymentMethodsState}
                  selectedPaymentMethod={flow.selectedPaymentMethod}
                  onPaymentMethodSelected={handlePaymentMethodSelected}
                  paymentReference={flow.paymentReference}
                  onPaymentReferenceChange={flow.setPaymentReference}
                  currency={currency}
                  totalAmountToPay={flow.totalAmountToPay}
                  invoiceSummary={flow.invoiceSummary}
                />
              }
            />
          ) : (
            <Center minHeight={220}>
              <Stack align="center" gap={3}>
                <Spinner />
                <T size="base" color="secondary">
                  Processing your payment…
                </T>
              </Stack>
            </Center>
          )}
        </ControlPresentationProvider>
      </Dialog>

      <PaymentsConfirmMakePaymentsDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={flow.confirmPayment}
        confirmText={paymentsConfirmButtonText(
          flow.paymentMethodsState,
          flow.selectedPaymentMethod,
        )}
        totalAmountToPay={flow.totalAmountToPay}
        currency={currency}
        selectedPaymentMethod={selectedPaymentMethodWithIcon}
        invoiceSummary={flow.invoiceSummary}
        paymentMethodText={paymentsChargeTimingText(
          flow.paymentMethodsState,
          flow.selectedPaymentMethod,
        )}
      />

      <PaymentsMakePaymentsReceiptDialog
        open={
          open &&
          (flow.status === PaymentsStatusType.Completed ||
            flow.status === PaymentsStatusType.Pending)
        }
        onOpenChange={closeFlow}
        paymentStatus={flow.status}
        totalAmount={flow.paidTotalAmount}
        currency={currency}
        selectedPaymentMethod={selectedPaymentMethodWithIcon}
        companyName={companyName}
        invoices={flow.paidInvoices}
        shipmentMap={flow.shipmentMap}
      />

      <PaymentsMakePaymentsFailedDialog
        open={open && flow.status === PaymentsStatusType.Failed}
        onOpenChange={closeFlow}
        errorMessage={flow.errorMessage}
      />

      <CreditCardsAddDialog
        open={addCardOpen}
        onOpenChange={setAddCardOpen}
        onCardAdded={() => {
          flow.reloadPaymentMethods();
        }}
      />
    </>
  );
};
